import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, UserRound, Loader2, CalendarCheck, ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "rosevale_chat_session";
const AUTO_OPEN_KEY = "rosevale_chat_auto_opened";
const AUTO_OPEN_MS = 3 * 60 * 1000;

const getSession = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/-/g, "").slice(0, 32);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const CALENDLY_URL = "https://calendly.com/abbie-browne";

const linkify = (text, onBook) =>
  text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) => {
    if (!/^https?:\/\//.test(part)) return part;
    if (part.includes("calendly.com") && onBook) {
      return (
        <button key={i} type="button" onClick={onBook} data-testid="chat-inline-book-button" className="inline-flex items-center gap-1.5 align-middle rounded-full bg-[#103080] text-white px-3 py-1 text-xs font-medium hover:bg-[#0090D0] transition-colors duration-300 mx-0.5">
          <CalendarCheck size={13} /> Pick a time
        </button>
      );
    }
    return (
      <a key={i} href={part} target={part.includes("rosevaleaccountants.co.uk") ? "_self" : "_blank"} rel="noreferrer" className="underline underline-offset-2 break-all hover:text-[#40C0E0]">
        {part.replace(/^https?:\/\/(www\.)?/, "")}
      </a>
    );
  });

// Calendly booking page embedded inside the chat panel
const BookingPane = ({ onBack, onBooked }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin === "https://calendly.com" && e.data?.event === "calendly.event_scheduled") onBooked();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onBooked]);
  const src = `${CALENDLY_URL}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_gdpr_banner=1&primary_color=103080&text_color=0f172a`;
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white" data-testid="chat-booking-pane">
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#103080]/10 bg-[#F5F2EB]">
        <button type="button" onClick={onBack} data-testid="chat-booking-back" className="inline-flex items-center gap-1 text-xs font-medium text-[#103080] hover:text-[#0090D0] transition-colors duration-300">
          <ArrowLeft size={14} /> Back to chat
        </button>
        <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="text-[11px] text-[#0F172A]/50 hover:text-[#0090D0] inline-flex items-center gap-1">
          Open in new tab <ArrowUpRight size={12} />
        </a>
      </div>
      <div className="relative flex-1 min-h-0">
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-[#0090D0]"><Loader2 size={22} className="animate-spin" /></div>
        )}
        <iframe
          title="Book a free discovery call"
          src={src}
          onLoad={() => setReady(true)}
          data-testid="chat-calendly-iframe"
          className="w-full h-full border-0"
        />
      </div>
    </div>
  );
};

const Bubble = ({ role, children }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        role === "user" ? "bg-[#103080] text-white rounded-br-md" : "bg-white border border-[#103080]/10 text-[#0F172A]/85 rounded-bl-md"
      }`}
    >
      {children}
    </div>
  </div>
);

const HandoffForm = ({ sessionId, page, onDone, onCancel }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/chat/handoff`, { session_id: sessionId, ...f, page });
      onDone(f.name);
    } catch {
      toast.error("Couldn't send that — please try again or email info@rosevaleaccountants.co.uk");
    } finally {
      setBusy(false);
    }
  };
  const cls = "w-full rounded-xl border border-[#103080]/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0090D0]";
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-[#103080]/10 p-4 space-y-3" data-testid="chat-handoff-form">
      <p className="text-sm font-medium text-[#103080]">Leave your details and a member of our lovely team will follow up within 1 working day.</p>
      <input className={cls} required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="chat-handoff-name" />
      <input className={cls} required type="email" placeholder="Email address" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="chat-handoff-email" />
      <input className={cls} placeholder="Phone number (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="chat-handoff-phone" />
      <textarea className={cls + " resize-none"} rows={3} placeholder="Your message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="chat-handoff-message" />
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="chat-handoff-submit" className="flex-1 rounded-full bg-[#103080] text-white text-sm font-medium py-2.5 hover:bg-[#0090D0] transition-colors duration-300 disabled:opacity-60">
          {busy ? "Sending…" : "Send to Rosevale"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-[#103080]/15 px-4 text-sm text-[#103080]">Cancel</button>
      </div>
    </form>
  );
};

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handedOff, setHandedOff] = useState(false);
  const [booking, setBooking] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const sessionId = useRef(getSession());
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const autoOpened = useRef(false);

  useEffect(() => {
    if (!open || loaded) return;
    (async () => {
      try {
        const [h, g] = await Promise.all([
          axios.get(`${API}/chat/history/${sessionId.current}`),
          axios.get(`${API}/chat/greeting`),
        ]);
        setMsgs(h.data.messages.length ? h.data.messages : [{ role: "assistant", content: g.data.greeting }]);
      } catch {
        setMsgs([{ role: "assistant", content: "Hi, I'm Rosie. Would you like help booking a discovery call? Or can I point you to one of our free guides? What can I help you with?" }]);
      }
      setLoaded(true);
    })();
  }, [open, loaded]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, showHandoff, open]);
  useEffect(() => { if (open && !autoOpened.current) setTimeout(() => inputRef.current?.focus(), 150); autoOpened.current = false; }, [open]);

  // Pop Rosie open once per browser session if the visitor is still around after 3 minutes
  useEffect(() => {
    if (open) sessionStorage.setItem(AUTO_OPEN_KEY, "1");
  }, [open]);
  useEffect(() => {
    if (sessionStorage.getItem(AUTO_OPEN_KEY)) return;
    const t = setTimeout(() => {
      if (!sessionStorage.getItem(AUTO_OPEN_KEY)) { autoOpened.current = true; setOpen(true); }
    }, AUTO_OPEN_MS);
    return () => clearTimeout(t);
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, page: pathname }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Chat unavailable");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = JSON.parse(p.slice(6));
          if (d.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d.delta }; return c; });
        }
      }
    } catch (err) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: err.message || "Sorry — something went wrong. You can reach us on 0117 321 0360." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Chat with us"}
        data-testid="chat-toggle-button"
        className="fixed bottom-5 right-5 z-[70] w-16 h-16 rounded-full bg-white border-[3px] border-[#103080] shadow-[0_12px_32px_-8px_rgba(16,48,128,0.5)] flex items-center justify-center overflow-hidden hover:-translate-y-0.5 hover:border-[#0090D0] transition-[border-color,transform] duration-300"
      >
        {open ? (
          <X size={22} className="text-[#103080]" />
        ) : (
          <img src="/rosie-head.png" alt="Rosie" className="w-[52px] h-[52px] object-contain translate-y-1.5" />
        )}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className={`fixed z-[70] bottom-24 right-5 left-5 sm:left-auto flex flex-col rounded-3xl bg-[#F5F2EB] border border-[#103080]/10 shadow-[0_24px_60px_-20px_rgba(16,48,128,0.45)] overflow-hidden ${
            booking ? "sm:w-[420px] h-[calc(100vh-7rem)] max-h-[760px]" : "sm:w-[380px] max-h-[min(640px,calc(100vh-7rem))]"
          }`}
        >
          <div className="bg-[#103080] text-white px-5 py-3.5 flex items-center gap-3">
            <img src="/rosie-head.png" alt="Rosie" className="w-11 h-11 rounded-full bg-white object-contain p-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-serif font-semibold text-base leading-tight">Rosie</p>
              <p className="text-[11px] text-white/60">{booking ? "Pick a time that suits you" : "Here to help"}</p>
            </div>
            <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
              <img src="/logo-mark-hd.png" alt="Rosevale Accountants" className="w-7 h-7 object-contain" />
            </span>
          </div>

          {booking ? (
            <BookingPane
              onBack={() => setBooking(false)}
              onBooked={() => {
                setBooking(false);
                setMsgs((m) => [...m, { role: "assistant", content: "Lovely — you're booked in. Calendly will email you the confirmation and a calendar invite. Anything else I can help with in the meantime?" }]);
              }}
            />
          ) : (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <Bubble key={i} role={m.role}>
                {m.content ? linkify(m.content, () => setBooking(true)) : <Loader2 size={16} className="animate-spin text-[#0090D0]" />}
              </Bubble>
            ))}
            {showHandoff && !handedOff && (
              <HandoffForm
                sessionId={sessionId.current}
                page={pathname}
                onCancel={() => setShowHandoff(false)}
                onDone={(name) => {
                  setHandedOff(true);
                  setShowHandoff(false);
                  setMsgs((m) => [...m, { role: "assistant", content: `Thanks ${name.split(" ")[0]} — I've passed your details to the team. Someone will be in touch within 1 working day. You'll get a quick confirmation email too.` }]);
                }}
              />
            )}
            <div ref={endRef} />
          </div>
          )}

          {!booking && (
          <div className="border-t border-[#103080]/10 bg-white/60 px-3 pt-2 pb-3">
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setBooking(true)}
                data-testid="chat-book-call-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#103080] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#0090D0] transition-colors duration-300"
              >
                <CalendarCheck size={13} /> Book a discovery call
              </button>
              {!handedOff && !showHandoff && (
                <button
                  type="button"
                  onClick={() => setShowHandoff(true)}
                  data-testid="chat-leave-details-button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/15 bg-white px-3 py-1.5 text-xs font-medium text-[#103080] hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-300"
                >
                  <UserRound size={13} /> Leave your details
                </button>
              )}
            </div>
            <form onSubmit={send} className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about our services, guides or booking…"
                data-testid="chat-input"
                className="flex-1 resize-none rounded-2xl border border-[#103080]/15 bg-white px-4 py-2.5 text-sm max-h-28 focus:outline-none focus:ring-2 focus:ring-[#0090D0]"
              />
              <button type="submit" disabled={busy || !input.trim()} aria-label="Send" data-testid="chat-send-button" className="w-10 h-10 shrink-0 rounded-full bg-[#103080] text-white flex items-center justify-center hover:bg-[#0090D0] transition-colors duration-300 disabled:opacity-40">
                <Send size={16} />
              </button>
            </form>
          </div>
          )}
        </div>
      )}
    </>
  );
};
